
(() => {
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const root = document.body;
  if (!root) return;

  const q = (s, c = document) => c.querySelector(s);
  const qa = (s, c = document) => [...c.querySelectorAll(s)];

  const reduce = !!(
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const focusable =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  let lastFocus = null;

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab' || !container) return;

    const items = qa(focusable, container).filter(
      el => el.offsetParent !== null
    );

    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  };

  const openLayer = (layer, focusTarget) => {
    if (!layer) return;

    lastFocus = document.activeElement;
    layer.hidden = false;
    root.classList.add('pp-lock');

    requestAnimationFrame(() =>
      (focusTarget?.() || q(focusable, layer))?.focus()
    );
  };

  const closeLayer = (layer, restore = true) => {
    if (!layer || layer.hidden) return;

    layer.hidden = true;

    if (
      ![
        q('[data-pp-mobile-drawer]'),
        q('[data-pp-search-panel]'),
        q('[data-pp-access-modal]')
      ].some(el => el && !el.hidden)
    ) {
      root.classList.remove('pp-lock');
    }

    if (restore && lastFocus instanceof HTMLElement) {
      lastFocus.focus();
    }
  };

  /* ========================================
     LIGHT / DARK MODE — SYNCHRONIZED FIX
     ======================================== */

  const storageKey = 'pp-theme';

  const preferred =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: light)').matches
      ? 'light'
      : 'dark';

  const configured = root.dataset.ppDefaultTheme || 'system';

  let saved = null;

  try {
    saved = localStorage.getItem(storageKey);
  } catch (e) {}

  const initialTheme =
    document.documentElement.getAttribute('data-pp-initial-theme');

  root.dataset.ppTheme =
    initialTheme === 'light' || initialTheme === 'dark'
      ? initialTheme
      : (
          saved === 'light' || saved === 'dark'
            ? saved
            : (
                configured === 'light' || configured === 'dark'
                  ? configured
                  : preferred
              )
        );

  const syncTheme = () => {
    document.documentElement.setAttribute(
      'data-pp-initial-theme',
      root.dataset.ppTheme
    );

    const themeMeta = q('meta[name="theme-color"]');

    if (themeMeta) {
      themeMeta.setAttribute(
        'content',
        root.dataset.ppTheme === 'light'
          ? '#f3f5f6'
          : '#080b0f'
      );
    }

    qa('[data-pp-theme-toggle]').forEach(t => {
      const dark = root.dataset.ppTheme === 'dark';

      t.setAttribute(
        'aria-label',
        dark ? 'Switch to light mode' : 'Switch to dark mode'
      );

      t.setAttribute(
        'aria-pressed',
        dark ? 'false' : 'true'
      );

      t.textContent = dark ? '☼' : '☾';
    });
  };

  syncTheme();

  qa('[data-pp-theme-toggle]').forEach(t => {
    t.addEventListener('click', () => {
      root.dataset.ppTheme =
        root.dataset.ppTheme === 'dark'
          ? 'light'
          : 'dark';

      try {
        localStorage.setItem(storageKey, root.dataset.ppTheme);
      } catch (e) {}

      syncTheme();
    });
  });

  /* ========================================
     ANNOUNCEMENT BAR
     ======================================== */

  const announcement = q('[data-pp-announcement]');

  if (announcement) {
    let dismissed = false;

    try {
      dismissed =
        !window.Shopify?.designMode &&
        sessionStorage.getItem('pp-announcement-dismissed') === '1';
    } catch (e) {}

    if (dismissed) {
      announcement.hidden = true;
    }

    q('[data-pp-announcement-close]', announcement)?.addEventListener(
      'click',
      () => {
        announcement.hidden = true;

        try {
          sessionStorage.setItem('pp-announcement-dismissed', '1');
        } catch (e) {}
      }
    );
  }

  /* ========================================
     HEADER SCROLL STATE
     ======================================== */

  const header = q('[data-pp-site-header]');
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;

    ticking = true;

    requestAnimationFrame(() => {
      header?.classList.toggle(
        'is-scrolled',
        window.scrollY > 12
      );

      ticking = false;
    });
  };

  onScroll();

  addEventListener('scroll', onScroll, {
    passive: true
  });

  /* ========================================
     AMBIENT PARTICLES
     ======================================== */

  function particleCanvas(canvas) {
    if (
      !canvas ||
      reduce ||
      root.dataset.ppMotion === 'false'
    ) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let dpr = Math.min(devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let pts = [];
    let raf = 0;
    let active = true;

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;

      canvas.width = Math.max(
        1,
        Math.floor(w * dpr)
      );

      canvas.height = Math.max(
        1,
        Math.floor(h * dpr)
      );

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(
        18,
        Math.min(64, Math.floor((w * h) / 28000))
      );

      pts = Array.from(
        { length: count },
        (_, i) => ({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - .5) * .13,
          vy: (Math.random() - .5) * .13,
          r: Math.random() * 1.25 + .3,
          hot: i % 6 === 0
        })
      );
    };

    const draw = () => {
      if (!active || document.hidden) {
        raf = requestAnimationFrame(draw);
        return;
      }

      ctx.clearRect(0, 0, w, h);

      const light = root.dataset.ppTheme === 'light';

      pts.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        if (p.y > h + 10) p.y = -10;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);

        ctx.fillStyle = p.hot
          ? 'rgba(0,255,56,.55)'
          : (
              light
                ? 'rgba(8,11,15,.16)'
                : 'rgba(255,255,255,.13)'
            );

        ctx.fill();

        if (p.hot) {
          for (let j = i + 1; j < pts.length; j++) {
            const o = pts[j];

            const d = Math.hypot(
              p.x - o.x,
              p.y - o.y
            );

            if (d < 120) {
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(o.x, o.y);

              ctx.strokeStyle =
                `rgba(0,255,56,${(1 - d / 120) * .07})`;

              ctx.lineWidth = .6;
              ctx.stroke();
            }
          }
        }
      });

      raf = requestAnimationFrame(draw);
    };

    const ro =
      'ResizeObserver' in window
        ? new ResizeObserver(resize)
        : null;

    ro?.observe(canvas);

    if (!ro) {
      addEventListener('resize', resize, {
        passive: true
      });
    }

    const io =
      'IntersectionObserver' in window
        ? new IntersectionObserver(([entry]) => {
            active = entry.isIntersecting;
          })
        : null;

    io?.observe(canvas);

    resize();
    draw();

    addEventListener(
      'pagehide',
      () => cancelAnimationFrame(raf),
      { once: true }
    );
  }

  qa('[data-pp-canvas],[data-pp-site-canvas]')
    .forEach(particleCanvas);

  /* ========================================
     POINTER-REACTIVE BACKGROUND / ORBIT
     ======================================== */

  if (!reduce && root.dataset.ppMotion !== 'false') {
    let pointerFrame = 0;
    let px = 50;
    let py = 50;

    addEventListener(
      'pointermove',
      e => {
        px = e.clientX / innerWidth * 100;
        py = e.clientY / innerHeight * 100;

        if (pointerFrame) return;

        pointerFrame = requestAnimationFrame(() => {
          qa('[data-pp-shell],[data-pp-site-shell]')
            .forEach(el => {
              el.style.setProperty(
                '--pp-cursor-x',
                px + '%'
              );

              el.style.setProperty(
                '--pp-cursor-y',
                py + '%'
              );
            });

          if (innerWidth > 900) {
            const rx = (py / 100 - .5) * -6;
            const ry = (px / 100 - .5) * 8;

            const portal = q('[data-pp-portal]');
            const orbit = q('[data-pp-home-orbit]');

            if (portal) {
              portal.style.transform =
                `rotateX(${rx}deg) rotateY(${ry}deg)`;
            }

            if (orbit) {
              orbit.style.transform =
                `rotateX(${rx * .72}deg) rotateY(${ry * .72}deg)`;
            }
          }

          pointerFrame = 0;
        });
      },
      { passive: true }
    );
  }

  /* ========================================
     HEADLINE SCRAMBLE ANIMATION
     ======================================== */

  const title = q('[data-pp-glitch]');

  if (
    title &&
    !reduce &&
    root.dataset.ppMotion !== 'false'
  ) {
    const original = title.textContent;
    const chars = '01/<>×+?*#';

    let interval = null;

    const scramble = () => {
      if (document.hidden || interval) return;

      let frame = 0;

      interval = setInterval(() => {
        title.textContent = original
          .split('')
          .map((ch, i) =>
            ch === ' '
              ? ' '
              : (
                  i < frame / 2
                    ? ch
                    : (
                        Math.random() > .7
                          ? chars[
                              Math.floor(
                                Math.random() * chars.length
                              )
                            ]
                          : ch
                      )
                )
          )
          .join('');

        frame++;

        if (frame > original.length * 2 + 4) {
          clearInterval(interval);
          interval = null;
          title.textContent = original;
        }
      }, 26);
    };

    setTimeout(scramble, 700);
    setInterval(scramble, 14000);
  }

  /* ========================================
     PASSWORD ACCESS MODAL
     ======================================== */

  const modal = q('[data-pp-access-modal]');
  const hideAccess = () => closeLayer(modal);

  q('[data-pp-access-open]')?.addEventListener(
    'click',
    () => openLayer(
      modal,
      () => q('input', modal)
    )
  );

  qa('[data-pp-access-close]').forEach(el => {
    el.addEventListener('click', hideAccess);
  });

  modal?.addEventListener(
    'keydown',
    e => trapFocus(
      e,
      q('[role="dialog"]', modal) || modal
    )
  );

  /* ========================================
     MOBILE DRAWER
     ======================================== */

  const drawer = q('[data-pp-mobile-drawer]');
  const menuOpen = q('[data-pp-menu-open]');

  const closeDrawer = (restore = true) => {
    if (drawer) {
      closeLayer(drawer, restore);
    }

    menuOpen?.setAttribute(
      'aria-expanded',
      'false'
    );

    q('[data-pp-filter-open]')?.setAttribute(
      'aria-expanded',
      'false'
    );
  };

  menuOpen?.addEventListener('click', () => {
    if (drawer) {
      menuOpen.setAttribute(
        'aria-expanded',
        'true'
      );

      openLayer(
        drawer,
        () => q('.pp-mobile-drawer__panel', drawer)
      );
    }
  });

  qa('[data-pp-menu-close]').forEach(el => {
    el.addEventListener('click', () => closeDrawer());
  });

  drawer?.addEventListener(
    'keydown',
    e => trapFocus(
      e,
      q('.pp-mobile-drawer__panel', drawer)
    )
  );

  /* ========================================
     SEARCH OVERLAY
     ======================================== */

  const searchPanel = q('[data-pp-search-panel]');

  const openSearch = () => openLayer(
    searchPanel,
    () => q('input', searchPanel)
  );

  const closeSearch = (restore = true) =>
    closeLayer(searchPanel, restore);

  qa('[data-pp-search-open]').forEach(el => {
    el.addEventListener('click', e => {
      e.preventDefault();
      closeDrawer(false);
      openSearch();
    });
  });

  qa('[data-pp-search-close]').forEach(el => {
    el.addEventListener(
      'click',
      () => closeSearch()
    );
  });

  searchPanel?.addEventListener(
    'keydown',
    e => trapFocus(
      e,
      q('.pp-search-panel__card', searchPanel)
    )
  );

  /* ========================================
     PRODUCT MAGNIFIER CONFIGURATION
     ======================================== */

  const ppStandaloneMagnifierSettings =
    document.querySelector('[data-pp-magnifier-config]');

  const ppProductMagnifierHost =
    document.querySelector('.pp-product-gallery__stage');

  if (
    ppStandaloneMagnifierSettings &&
    ppProductMagnifierHost
  ) {
    if (
      ppStandaloneMagnifierSettings.dataset.enabled === 'true'
    ) {
      ppProductMagnifierHost.dataset.ppMagnify = 'product';

      ppProductMagnifierHost.dataset.ppMagnifyZoom =
        ppStandaloneMagnifierSettings.dataset.zoom || '225';
    } else {
      delete ppProductMagnifierHost.dataset.ppMagnify;
      delete ppProductMagnifierHost.dataset.ppMagnifyZoom;
    }
  }

  /* ========================================
     PRODUCT MAGNIFIER
     ALWAYS USE CURRENT IMAGE
     ======================================== */

  const ppMagnifiers = [
    ...document.querySelectorAll('[data-pp-magnify]')
  ];

  const ppPrecisePointer = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  );

  const ppClamp = (value, min, max) =>
    Math.max(min, Math.min(max, value));

  const ppGetMagnifiedImage = host => {
    if (host.dataset.ppMagnify === 'card') {
      return host.querySelector(
        'img:not(.pp-product-card__second)'
      );
    }

    const active = host.querySelector(
      '.pp-product-media.is-active:not([hidden])'
    );

    return active?.querySelector('img') || null;
  };

  const ppLens = new Map();

  const ppHideMagnifier = host => {
    const lens = ppLens.get(host);

    if (lens) {
      lens.classList.remove('is-active');
    }
  };

  ppMagnifiers.forEach(host => {
    let lens;

    const zoom = ppClamp(
      Number(host.dataset.ppMagnifyZoom || 225) / 100,
      1.5,
      3
    );

    const showAt = ev => {
      if (
        !ppPrecisePointer.matches ||
        (
          ev.pointerType &&
          ev.pointerType !== 'mouse' &&
          ev.pointerType !== 'pen'
        )
      ) {
        ppHideMagnifier(host);
        return;
      }

      const image = ppGetMagnifiedImage(host);

      if (
        !image ||
        !image.complete ||
        !image.naturalWidth ||
        !image.naturalHeight
      ) {
        ppHideMagnifier(host);
        return;
      }

      const bounds = host.getBoundingClientRect();
      const imageBounds = image.getBoundingClientRect();

      if (
        bounds.width <= 0 ||
        bounds.height <= 0 ||
        imageBounds.width <= 0 ||
        imageBounds.height <= 0
      ) {
        return;
      }

      const computed = getComputedStyle(image);
      const fit = computed.objectFit || 'fill';

      const scaleX =
        imageBounds.width / image.naturalWidth;

      const scaleY =
        imageBounds.height / image.naturalHeight;

      let scale =
        fit === 'contain'
          ? Math.min(scaleX, scaleY)
          : fit === 'cover'
            ? Math.max(scaleX, scaleY)
            : null;

      const visibleWidth =
        scale === null
          ? imageBounds.width
          : image.naturalWidth * scale;

      const visibleHeight =
        scale === null
          ? imageBounds.height
          : image.naturalHeight * scale;

      const offsetX =
        (imageBounds.width - visibleWidth) / 2;

      const offsetY =
        (imageBounds.height - visibleHeight) / 2;

      const withinImageX =
        ev.clientX - imageBounds.left - offsetX;

      const withinImageY =
        ev.clientY - imageBounds.top - offsetY;

      if (
        withinImageX < 0 ||
        withinImageX > visibleWidth ||
        withinImageY < 0 ||
        withinImageY > visibleHeight
      ) {
        ppHideMagnifier(host);
        return;
      }

      lens ||= document.createElement('span');

      if (!ppLens.has(host)) {
        lens.className = 'pp-magnifier';
        lens.setAttribute('aria-hidden', 'true');

        host.appendChild(lens);
        ppLens.set(host, lens);
      }

      const url = image.currentSrc || image.src;

      if (lens.dataset.source !== url) {
        lens.dataset.source = url;

        lens.style.backgroundImage =
          `url("${url.replace(/"/g, '%22')}")`;
      }

      const lensSize =
        lens.getBoundingClientRect().width ||
        (
          host.dataset.ppMagnify === 'card'
            ? 150
            : 200
        );

      lens.style.left =
        `${ev.clientX - bounds.left}px`;

      lens.style.top =
        `${ev.clientY - bounds.top}px`;

      lens.style.backgroundSize =
        `${visibleWidth * zoom}px ${visibleHeight * zoom}px`;

      lens.style.backgroundPosition =
        `${lensSize / 2 - withinImageX * zoom}px ` +
        `${lensSize / 2 - withinImageY * zoom}px`;

      lens.classList.add('is-active');
    };

    host.addEventListener(
      'pointermove',
      showAt,
      { passive: true }
    );

    host.addEventListener(
      'pointerleave',
      () => ppHideMagnifier(host)
    );

    host.addEventListener(
      'pointercancel',
      () => ppHideMagnifier(host)
    );

    host.addEventListener(
      'focusout',
      () => ppHideMagnifier(host)
    );
  });

  /* ========================================
     PRODUCT GALLERY
     COLOUR-BASED IMAGE SELECTION
     ======================================== */

  const mediaBtns = qa('[data-pp-media-target]');

  const ppProductGallery =
    q('[data-pp-product-gallery]');

  const ppGalleryStatus = q(
    '[data-pp-gallery-colour-status]',
    ppProductGallery || document
  );

  const ppGalleryMedia = mediaBtns.map(btn => ({
    id: btn.dataset.ppMediaTarget,
    src: btn.dataset.ppMediaSrc || '',
    alt: btn.dataset.ppMediaAlt || ''
  }));

  let ppCurrentMediaId = '';

  const activateMedia = btn => {
    const gallery = btn
      .closest('.pp-product-gallery')
      ?.querySelector('[data-pp-magnify="product"]');

    if (gallery) {
      ppHideMagnifier(gallery);
    }

    ppCurrentMediaId = btn.dataset.ppMediaTarget;

    qa('[data-pp-media-id]').forEach(el => {
      const active =
        el.dataset.ppMediaId === btn.dataset.ppMediaTarget;

      el.hidden = !active;
      el.classList.toggle('is-active', active);

      if (!active) {
        qa('video', el).forEach(v => v.pause?.());
      }
    });

    mediaBtns.forEach(b => {
      const active = b === btn;

      b.classList.toggle('is-active', active);

      b.setAttribute(
        'aria-pressed',
        active ? 'true' : 'false'
      );
    });
  };

  mediaBtns.forEach(btn => {
    btn.addEventListener(
      'click',
      () => activateMedia(btn)
    );
  });

  /* ========================================
     PRODUCT VARIANTS
     ======================================== */

  const variantsEl = q('[data-pp-product-json]');

  if (variantsEl) {
    let variants = [];

    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch (e) {}

    const groups = qa('[data-pp-option-index]');
    const hidden = q('[data-pp-variant-id]');
    const price = q('[data-pp-product-price]');
    const compare = q('[data-pp-product-compare]');
    const stock = q('[data-pp-stock]');
    const add = q('[data-pp-add-to-cart]');
    const discountBadge = q('[data-pp-discount-badge]');

    const selectedOptions = () =>
      groups.map(
        g =>
          q('.pp-option-value.is-active', g)
            ?.dataset.ppOptionValue || ''
      );

    const ppUpdateColourGallery = (variant, selected) => {
      if (
        !mediaBtns.length ||
        !window.PPColourGallery
      ) {
        return;
      }

      const result = window.PPColourGallery.plan({
        media: ppGalleryMedia,
        variants,
        optionNames: groups.map(
          g => g.dataset.ppOptionName || ''
        ),
        selectedOptions: selected,
        selectedVariant: variant,
        activeMediaId: ppCurrentMediaId,
        includeUntagged:
          ppProductGallery?.dataset.ppShowUntagged === 'true',
        enabled:
          ppProductGallery?.dataset.ppFilterColour !== 'false'
      });

      const shown = new Set(result.ids);

      mediaBtns.forEach(btn => {
        const visible = shown.has(
          btn.dataset.ppMediaTarget
        );

        btn.hidden = !visible;

        btn.setAttribute(
          'aria-hidden',
          visible ? 'false' : 'true'
        );

        btn.tabIndex = visible ? 0 : -1;
      });

      const target =
        mediaBtns.find(
          btn =>
            btn.dataset.ppMediaTarget === result.activeId &&
            !btn.hidden
        ) ||
        mediaBtns.find(btn => !btn.hidden);

      if (
        target &&
        (
          ppCurrentMediaId !== target.dataset.ppMediaTarget ||
          !target.classList.contains('is-active')
        )
      ) {
        activateMedia(target);
      }

      if (ppGalleryStatus) {
        ppGalleryStatus.hidden = !result.filtered;

        if (result.filtered) {
          const colour = selected.find(
            (_, idx) =>
              /(?:colou?r|fabric)/i.test(
                groups[idx]?.dataset.ppOptionName || ''
              )
          ) || 'Selected colour';

          ppGalleryStatus.textContent =
            `${colour} · ${result.matched} photo` +
            `${result.matched === 1 ? '' : 's'}`;
        }
      }
    };

    const renderVariant = () => {
      const selected = selectedOptions();

      let v = variants.find(
        x =>
          JSON.stringify(x.options) ===
          JSON.stringify(selected)
      );

      if (!v && variants.length === 1) {
        v = variants[0];
      }

      if (!v) {
        ppUpdateColourGallery(null, selected);

        if (add) {
          add.disabled = true;
          add.textContent = 'UNAVAILABLE';
        }

        if (stock) {
          stock.innerHTML =
            '<i></i><b>Combination unavailable</b>';
        }

        if (compare) {
          compare.hidden = true;
        }

        if (
          discountBadge &&
          discountBadge.dataset.ppDiscountMode === 'automatic'
        ) {
          discountBadge.hidden = true;
        }

        return;
      }

      if (hidden) {
        hidden.value = v.id;
      }

      if (price) {
        price.textContent = v.price;
      }

      if (compare) {
        compare.hidden = !v.compare_at_price;
        compare.textContent =
          v.compare_at_price || '';
      }

      if (
        discountBadge &&
        discountBadge.dataset.ppDiscountMode === 'automatic'
      ) {
        const compareCents = Number(
          v.compare_at_price_cents || 0
        );

        const priceCents = Number(
          v.price_cents || 0
        );

        const onSale =
          v.available &&
          compareCents > priceCents &&
          priceCents >= 0;

        discountBadge.hidden = !onSale;

        if (onSale) {
          const pct = Math.round(
            (compareCents - priceCents) *
            100 / compareCents
          );

          const value = q(
            '[data-pp-discount-value]',
            discountBadge
          );

          const label = q(
            '[data-pp-discount-label]',
            discountBadge
          );

          if (value) {
            value.textContent = `-${pct}%`;
          }

          if (label) {
            label.textContent =
              discountBadge.dataset.ppDiscountAutoLabel ||
              'OFF';
          }
        }
      }

      const qty = Number(v.inventory_quantity || 0);
      const tracked = !!v.inventory_management;

      const exact =
        stock?.dataset.showExact === 'true';

      if (stock) {
        if (!v.available) {
          stock.innerHTML =
            '<i></i><b>Sold out</b>';
        } else if (
          tracked &&
          qty > 0 &&
          qty <= Number(
            getComputedStyle(root).getPropertyValue(
              '--pp-low-stock'
            ) || 5
          )
        ) {
          stock.innerHTML =
            `<em></em><b>${
              exact
                ? `${qty} piece${qty === 1 ? '' : 's'} left`
                : 'Limited stock'
            }</b>`;
        } else {
          stock.innerHTML =
            '<span></span><b>Ready to order</b>';
        }
      }

      if (add) {
        add.disabled = !v.available;

        add.textContent = v.available
          ? (
              add.dataset.defaultLabel ||
              'ADD TO BAG →'
            )
          : 'SOLD OUT';
      }

      ppUpdateColourGallery(v, selected);

      if (history.replaceState && v.url) {
        history.replaceState({}, '', v.url);
      }
    };

    groups.forEach(g => {
      qa('[data-pp-option-value]', g).forEach(btn => {
        btn.addEventListener('click', () => {
          qa('[data-pp-option-value]', g).forEach(b => {
            b.classList.toggle(
              'is-active',
              b === btn
            );

            b.setAttribute(
              'aria-pressed',
              b === btn ? 'true' : 'false'
            );
          });

          renderVariant();
        });
      });
    });

    renderVariant();
  }

  /* ========================================
     ADD TO CART FEEDBACK
     ======================================== */

  qa('[data-pp-product-form]').forEach(form => {
    form.addEventListener('submit', () => {
      const add = q(
        '[data-pp-add-to-cart]',
        form
      );

      if (add && !add.disabled) {
        add.setAttribute(
          'aria-busy',
          'true'
        );

        add.textContent = 'ADDING…';

        setTimeout(() => {
          add.removeAttribute('aria-busy');

          if (!add.disabled) {
            add.textContent =
              add.dataset.defaultLabel ||
              'ADD TO BAG →';
          }
        }, 5000);
      }
    });
  });

  /* ========================================
     SIZE GUIDE DIALOGS
     ======================================== */

  qa('[data-pp-size-guide]').forEach(guide => {
    const dialog = q(
      '.pp-size-guide__dialog',
      guide
    );

    const open = () =>
      openLayer(
        guide,
        () => dialog
      );

    const close = () =>
      closeLayer(guide);

    const trigger =
      q('[data-pp-size-guide-open]');

    trigger?.addEventListener(
      'click',
      open
    );

    qa('[data-pp-size-guide-close]', guide).forEach(el => {
      el.addEventListener(
        'click',
        close
      );
    });

    guide.addEventListener(
      'keydown',
      e => trapFocus(e, dialog)
    );

    qa('[data-pp-size-tab]', guide).forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.ppSizeTab;

        qa('[data-pp-size-tab]', guide).forEach(btn => {
          const active = btn === tab;

          btn.classList.toggle(
            'is-active',
            active
          );

          btn.setAttribute(
            'aria-selected',
            active ? 'true' : 'false'
          );
        });

        qa('[data-pp-size-panel]', guide).forEach(panel => {
          panel.hidden =
            panel.dataset.ppSizePanel !== target;
        });
      });
    });
  });

  /* ========================================
     COLLECTION FILTER DRAWER
     ======================================== */

  const filter = q('[data-pp-filter-panel]');
  const filterOpen = q('[data-pp-filter-open]');

  const closeFilter = () => {
    if (filter) {
      filter.classList.remove('is-open');
    }

    root.classList.remove('pp-lock');

    filterOpen?.setAttribute(
      'aria-expanded',
      'false'
    );
  };

  filterOpen?.addEventListener('click', () => {
    if (filter) {
      filter.classList.add('is-open');
      root.classList.add('pp-lock');

      filterOpen.setAttribute(
        'aria-expanded',
        'true'
      );

      setTimeout(() => {
        q('button,input,select', filter)?.focus();
      }, 20);
    }
  });

  q('[data-pp-filter-close]')?.addEventListener(
    'click',
    closeFilter
  );

  q(
    '[data-pp-filter-form] select[name="sort_by"]'
  )?.addEventListener('change', e => {
    if (innerWidth >= 990) {
      e.currentTarget.form?.submit();
    }
  });

  /* ========================================
     PROGRESSIVE REVEAL ANIMATIONS
     ======================================== */

  const revealEls = qa('[data-pp-reveal]');

  if (
    !reduce &&
    'IntersectionObserver' in window
  ) {
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      {
        threshold: .08,
        rootMargin: '0px 0px -20px'
      }
    );

    revealEls.forEach(el => io.observe(el));
  } else {
    revealEls.forEach(el => {
      el.classList.add('is-revealed');
    });
  }

  /* ========================================
     GLOBAL ESCAPE / KEYBOARD SEARCH
     ======================================== */

  addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      hideAccess();
      closeDrawer();
      closeSearch();
      closeFilter();

      qa('[data-pp-size-guide]').forEach(g => {
        closeLayer(g);
      });
    }

    if (
      (e.metaKey || e.ctrlKey) &&
      e.key.toLowerCase() === 'k'
    ) {
      e.preventDefault();
      openSearch();
    }
  });
})();
